/**
 * Theme preference handling.
 *
 * Stored in `localStorage` under `sazeo.theme`. Three valid values:
 *   - `light`  — force light mode
 *   - `dark`   — force dark mode
 *   - `system` — follow `prefers-color-scheme` media query live
 *
 * Applied by setting `data-theme="light|dark"` on `<html>` which is what
 * `@sazeo/design-tokens` reacts to.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'sazeo.theme';
const LEGACY_THEME_STORAGE_KEY = 'sazeo-theme';
const DEFAULT_THEME: ThemePreference = 'light';
const VALID: ReadonlySet<ThemePreference> = new Set(['light', 'dark', 'system']);
let detachSystemThemeListener: (() => void) | null = null;

export function readThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const raw =
    window.localStorage.getItem(THEME_STORAGE_KEY) ??
    window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
  return raw && VALID.has(raw as ThemePreference) ? (raw as ThemePreference) : DEFAULT_THEME;
}

export function writeThemePreference(pref: ThemePreference): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  window.localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
}

function systemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(pref: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const effective = pref === 'system' ? systemTheme() : pref;
  document.documentElement.setAttribute('data-theme', effective);
  document.documentElement.style.colorScheme = effective;
}

function clearSystemThemeListener(): void {
  if (!detachSystemThemeListener) return;
  detachSystemThemeListener();
  detachSystemThemeListener = null;
}

function observeSystemTheme(): void {
  if (typeof window === 'undefined' || !window.matchMedia) return;

  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => applyTheme('system');

  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    detachSystemThemeListener = () => mql.removeEventListener('change', handler);
    return;
  }

  const legacy = mql as MediaQueryList & {
    addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    removeListener?: (listener: (event: MediaQueryListEvent) => void) => void;
  };
  legacy.addListener?.(handler);
  detachSystemThemeListener = () => legacy.removeListener?.(handler);
}

export function syncThemePreference(pref: ThemePreference): void {
  clearSystemThemeListener();
  writeThemePreference(pref);
  applyTheme(pref);
  if (pref === 'system') observeSystemTheme();
}

/**
 * Boot-time theme apply. Also wires a `matchMedia` listener when the
 * preference is `system` so the UI flips without a reload.
 *
 * Returns an unsubscribe function — callers that want to rewire after a
 * user change should dispose the previous subscription first.
 */
export function bootTheme(): () => void {
  const pref = readThemePreference();
  syncThemePreference(pref);
  return () => clearSystemThemeListener();
}
